import { z } from 'zod';
import { Button, Stack } from '@optimus-web/core';
import {
    createZodFormResolver,
    CustomErrorParams,
    CustomErrorType,
    LocalizedText,
    Form,
    Validation,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { PasswordField } from '@mews-commander/form';
import { OverlayedLoader } from '@mews-commander/ui';

enum FormField {
    Password = 'password',
    NewPassword = 'newPassword',
}

const schema = z
    .object({
        [FormField.Password]: Validation.String.Password,
        [FormField.NewPassword]: z.string(),
    })
    .refine(({ password, newPassword }) => password === newPassword, {
        params: {
            errorType: CustomErrorType.Other,
            textKey: TextKey.PasswordsMustBeTheSame,
        } satisfies CustomErrorParams,
        path: [FormField.NewPassword],
    });

type SetPasswordFormValues = z.infer<typeof schema>;

const setPasswordResolver = createZodFormResolver(schema);

export const SetPasswordForm = ({ onFormSuccess }: { onFormSuccess: () => void }) => {
    const { mutate, isPending } = useRepositoryMutation('generalUsers', 'setPassword');

    const onSubmit = ({ newPassword }: SetPasswordFormValues) => {
        mutate({ newPassword }, { onSuccess: () => onFormSuccess() });
    };

    return (
        <>
            <OverlayedLoader show={isPending} />

            <Form onSubmit={onSubmit} resolver={setPasswordResolver}>
                <Stack vertical spacing="200">
                    <PasswordField
                        label={<LocalizedText textKey={TextKey.NewPassword} />}
                        helper={<LocalizedText textKey={TextKey.PasswordRequirementsErrorMessage} />}
                        name={FormField.Password}
                        autoComplete="new-password"
                        hasPasswordVisibilityToggle
                        showPasswordStrength
                        required
                    />

                    <PasswordField
                        label={<LocalizedText textKey={TextKey.NewPasswordCheck} />}
                        name={FormField.NewPassword}
                        autoComplete="new-password"
                        hasPasswordVisibilityToggle
                        required
                    />

                    <Button type="submit" variant="primary" wide disabled={isPending}>
                        <LocalizedText textKey={TextKey.Reset} />
                    </Button>
                </Stack>
            </Form>
        </>
    );
};
